import { useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BiChevronLeft } from 'react-icons/bi';
import { FiCheckCircle, FiClock, FiEye, FiPlus, FiXCircle } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/app/hook';
import { useGetPackagesByNameQuery, useLazyGetPackageDetailQuery, useUpdatePackageStatusMutation } from '@/app/services/package-rtk.service';
import TemplateCard from '@/components/common/TemplateCard';
import { PSButton } from '@/components/ui/button';
import { MultiLanguageUrl } from '@/constants/multiLanguage.constant';
import { selectUseId } from '@/features/auth/auth.slice';
import SkeletonLoading from '@/features/scenario/create/components/marketplace/SkeletonLoading';
import { resetCreateFlow, setType, startVersionCreation } from '@/features/scenario/create/create.slice';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';
import Utilities from '@/utils';
import { BotTypeEnum } from '@/utils/BotTypeEnum';

interface IState {
  query: string;
  size: string;
  page: string;
}

// Define the action types
type Action = { type: 'SET_SIZE'; payload: string } | { type: 'SET_PAGE'; payload: string };

// Define the initial state
const initialState: IState = {
  query: '',
  page: '1',
  size: '10',
};

// Define the reducer
const reducer = (state: IState, action: Action): IState => {
  switch (action.type) {
    case 'SET_PAGE':
      return { ...state, page: action.payload };
    case 'SET_SIZE':
      return { ...state, size: action.payload };
    default:
      return state;
  }
};

function PackageVersionList() {
  const dispatch = useAppDispatch();
  const queryParams = new URLSearchParams(location.search);
  const package_name = queryParams.get('package_name');
  const userId = useAppSelector(selectUseId);
  const [state, dispatchState] = useReducer(reducer, initialState);

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isLoading, isError, isSuccess, refetch, isFetching, data } = useGetPackagesByNameQuery({
    query: package_name || '',
    limit: state.size,
    page: state.page,
  });
  const [updatePackageStatus, updaetePackageResult] = useUpdatePackageStatusMutation();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);
  const [getLastVersion, getLastVersionResult] = useLazyGetPackageDetailQuery();
  const handlePublishClick = (package_id: string) => {
    setSelectedPackageId(package_id);
    setShowConfirmModal(true);
  };

  const confirmPublish = async () => {
    if (selectedPackageId) {
      try {
        await updatePackageStatus({ package_id: selectedPackageId, status: 'PUBLISHED' }).unwrap();
        refetch();
      } catch (error) {
        console.error('Failed to publish package:', error);
      }
    }
    setShowConfirmModal(false);
    setSelectedPackageId(null);
  };

  const cancelPublish = () => {
    setShowConfirmModal(false);
    setSelectedPackageId(null);
  };

  const addNewVersion = () => {
    if (!data?.packages || data.packages.length === 0) return;
    const sortedPackages = [...data.packages].sort((a, b) => Number(b.version) - Number(a.version));
    const last_package = sortedPackages[0];
    if (!last_package) return;
    getLastVersion(last_package.package_id).then(res => {
      const stepData = {
        name: package_name,
        type: res?.data?.type === 'VIDEOBOT' ? 'video' : 'text',
        version: Number(last_package.version) + 1,
      };
      dispatch(resetCreateFlow());
      dispatch(setType(BotTypeEnum.MARKETPLACE));
      dispatch(startVersionCreation(stepData));
    });
  };
  let content;
  if (isError) {
    content = (
      <div className="tw-flex tw-justify-center tw-items-center tw-py-20">
        <div className="tw-text-center">
          <p className="tw-text-red-500 tw-mb-4 tw-text-lg">{t('PackageVersionList/error')}</p>
          <PSButton
            id="btn-package-refetch"
            onClick={refetch}
            title={t('PackageVersionList/reload')}
            className="tw-bg-blue-600 tw-text-white tw-px-4 tw-py-2 tw-rounded-lg hover:tw-bg-blue-700 tw-border-none"
            variant="none"
          />
        </div>
      </div>
    );
  } else if (isSuccess && (!data?.packages || data.packages.length === 0)) {
    content = (
      <div className="tw-flex tw-justify-center tw-items-center tw-py-20">
        <div className="tw-text-center">
          <div className="tw-text-6xl tw-mb-4">📦</div>
          <h3 className="tw-text-xl tw-font-semibold tw-text-gray-900 tw-mb-2">No versions found</h3>
          <p className="tw-text-gray-600 tw-mb-6">This package doesn&apos;t have any versions yet.</p>
        </div>
      </div>
    );
  } else if (isSuccess) {
    const { packages } = data;
    content = (
      <div className="tw-space-y-6">
        {packages?.map(item => {
          const isOld = item.status === 'OLD_VERSION';
          const isSubmitted = item.status === 'SUBMITTED';
          const isPublished = item.status === 'PUBLISHED';

          return (
            <div
              key={item.package_id}
              className={`tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-lg tw-overflow-hidden tw-transition-all tw-duration-200 ${
                isOld ? 'tw-opacity-60' : 'hover:tw-shadow-xl hover:tw-border-gray-300'
              }`}
            >
              <div className="tw-flex tw-flex-col lg:tw-flex-row">
                {/* Left Side - Package Info and Actions */}
                <div className="tw-flex-1 tw-p-6 tw-flex tw-flex-col">
                  {/* Package Header */}
                  <div className="tw-flex tw-flex-col md:tw-flex-row tw-justify-start  md:tw-justify-between">
                    <div className="tw-flex tw-items-start tw-gap-4 tw-mb-4">
                      <div className="tw-w-12 tw-h-12 tw-bg-blue-50 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                        <span className="tw-text-2xl">📦</span>
                      </div>
                      <div className="tw-flex-1 tw-min-w-0">
                        <h3 className="tw-font-semibold tw-text-gray-900 tw-text-lg tw-line-clamp-2 tw-leading-tight tw-font-['Lexend'] tw-mb-2">
                          {item.name}
                        </h3>

                        {/* Version and Status */}
                        <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mb-4">
                          <span className="tw-inline-flex tw-items-center tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium tw-bg-blue-50 tw-text-blue-700">
                            {t('PackageVersionList/version')}: {item.version}
                          </span>
                          <span
                            className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium ${Utilities.renderStatusBadge(
                              item?.status
                            )}`}
                          >
                            {isPublished ? (
                              <FiCheckCircle className="tw-w-3 tw-h-3" />
                            ) : isSubmitted ? (
                              <FiClock className="tw-w-3 tw-h-3" />
                            ) : (
                              <FiXCircle className="tw-w-3 tw-h-3" />
                            )}
                            {Utilities.renderVersionStatusTitle(item.status, t)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="tw-text-left md:tw-text-right tw-flex tw-flex-col tw-items-start md:tw-items-end tw-gap-3 tw-font-normal tw-font-['Lexend'] tw-mb-3">
                      <div className="tw-text-gray-700 tw-text-sm">
                        {t('marketplace/payment/total')} : ${item?.total}
                      </div>
                      <div className="tw-text-gray-700 tw-text-sm">
                        {t('marketplace/payment/collectable')} : ${item?.collectable}
                      </div>
                    </div>
                  </div>

                  {/* Spacer to push buttons to bottom */}
                  <div className="tw-flex-1"></div>

                  {/* Action Buttons */}
                  <div className="tw-flex tw-flex-col tw-gap-3 tw-mt-auto">
                    {item?.status === 'REJECTED' && (
                      <PSButton
                        id="btn-version-view"
                        onClick={() => {
                          navigate(`/${MultiLanguageUrl[i18n.language]}/control-panel/marketplace/detail?package_id=${item.package_id}`);
                        }}
                        className="tw-bg-blue-600 tw-text-white tw-px-4 tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-blue-700 tw-transition-colors tw-duration-200 tw-border-none tw-flex tw-items-center tw-justify-center tw-gap-2 tw-w-fit"
                        variant="none"
                        icon={<FiEye className="tw-w-4 tw-h-4" />}
                        title={t('PackageVersionList/view')}
                      />
                    )}

                    {isSubmitted && (
                      <PSButton
                        id="btn-version-publish"
                        isLoading={updaetePackageResult.isLoading && updaetePackageResult.originalArgs?.package_id === item.package_id}
                        onClick={() => handlePublishClick(item.package_id)}
                        className="tw-w-full tw-bg-green-600 tw-text-white tw-px-4 tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-green-700 tw-transition-colors tw-duration-200 tw-border-none tw-flex tw-items-center tw-justify-center tw-gap-2"
                        variant="none"
                        icon={<FiCheckCircle className="tw-w-4 tw-h-4" />}
                        title={t('PackageVersionList/publish')}
                      />
                    )}
                  </div>
                </div>

                {/* Right Side - Package Preview */}
                <div className="tw-w-full lg:tw-w-80 tw-bg-gray-50 tw-border-t lg:tw-border-t-0 lg:tw-border-l tw-border-gray-200 tw-p-4">
                  <div className="tw-h-full tw-flex tw-items-center tw-justify-center">
                    <div className="tw-w-full tw-max-w-xs">
                      <TemplateCard
                        item={{
                          name: item.name,
                          package_public_name: item.package_public_name || item.name,
                          version: item.version,
                          price: item.price || 0,
                          type: item.type,
                          package_id: item.package_id,
                          configuration: item.configuration || {},
                          data: item.data || {},
                          downloadCount: item.allTransactionCount,
                          publisher: {
                            name: userId,
                            icon: null,
                          },
                        }}
                        isSelected={false}
                        onClick={() => {
                          // Disabled action
                        }}
                        className="tw-h-full"
                        variant="default"
                        disableAction={true}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="tw-min-h-screen tw-bg-gray-50  tw-font-normal tw-font-['Lexend']">
      <div className="tw-max-w-7xl tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-py-8">
        {/* Back Button */}
        <PSButton
          id="btn-version-back"
          variant="none"
          icon={<BiChevronLeft size={24} />}
          onClick={() => navigate(-1)}
          className="tw-flex tw-items-center tw-gap-1 tw-border-none tw-bg-transparent tw-text-gray-700 hover:tw-text-gray-900 tw-pl-0 tw-mb-6"
          title={t('Wizard/general/key6')}
        />

        {/* Header Section */}
        <div className="tw-mb-8 tw-grid tw-grid-cols-1 tw-gap-4">
          <div className="tw-flex tw-flex-col sm:tw-flex-row tw-items-start sm:tw-items-center tw-justify-between tw-gap-4">
            <div className="tw-grid tw-grid-cols-1">
              <h1 className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-2">
                📦 {t('PackageVersionList/versioning')} {package_name}
              </h1>
              <p className="tw-text-gray-600">{t('PackageVersionList/description')}</p>
            </div>
            <div className="tw-grid tw-justify-items-end">
              <PSButton
                id="btn-version-add"
                isLoading={getLastVersionResult.isLoading}
                icon={<FiPlus />}
                onClick={() => addNewVersion()}
                className="tw-bg-blue-600 tw-text-white tw-px-6 tw-py-3 tw-rounded-lg hover:tw-bg-blue-700 tw-transition-all tw-duration-200 tw-shadow-sm tw-font-semibold tw-border-none tw-flex tw-items-center tw-gap-2 tw-whitespace-nowrap"
                variant="none"
                title={t('PackageVersionList/createNewVersion')}
              />
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-200 tw-overflow-hidden tw-min-h-[600px]">
          {isLoading || isFetching ? (
            <div className="tw-p-6">
              <SkeletonLoading length={6} />
            </div>
          ) : (
            <div className="tw-p-6">{content}</div>
          )}
        </div>

        {/* Pagination */}
        {isSuccess && data.pagination && (
          <div className="tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-200 tw-p-6 tw-mt-6">
            <div className="tw-flex tw-flex-col sm:tw-flex-row tw-items-center tw-justify-between tw-gap-4">
              <p className="tw-text-sm tw-text-gray-600">
                {t('PackageVersionList/page')} {data.pagination.page} {t('PackageVersionList/of')} {data.pagination.total_pages} — {data.pagination.total}{' '}
                {t('PackageVersionList/totalItems')}
              </p>
              <div className="tw-flex tw-gap-2">
                <PSButton
                  id="btn-version-prev"
                  size="sm"
                  variant="outline"
                  disabled={!data.pagination.has_prev_page}
                  className="tw-px-4 tw-py-2 tw-text-sm"
                  onClick={() => dispatchState({ type: 'SET_PAGE', payload: String(Number(state.page) - 1) })}
                >
                  {t('PackageVersionList/previous')}
                </PSButton>
                <PSButton
                  id="btn-version-next"
                  size="sm"
                  variant="outline"
                  disabled={!data.pagination.has_next_page}
                  className="tw-px-4 tw-py-2 tw-text-sm"
                  onClick={() => dispatchState({ type: 'SET_PAGE', payload: String(Number(state.page) + 1) })}
                >
                  {t('PackageVersionList/next')}
                </PSButton>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50">
          <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-p-8 tw-max-w-sm tw-w-full tw-mx-4">
            <h2 className="tw-text-xl tw-font-bold tw-mb-4 tw-text-gray-900">{t('PackageVersionList/confirmationTitle')}</h2>
            <p className="tw-text-gray-600 tw-mb-6">{t('PackageVersionList/confirmationMessage')}</p>
            <div className="tw-flex tw-justify-end tw-gap-4">
              <PSButton id="btn-version-cancel" variant="outline" onClick={cancelPublish} className="tw-px-4 tw-py-2 tw-text-sm">
                {t('PackageVersionList/cancel')}
              </PSButton>
              <PSButton
                id="btn-version-confirm"
                onClick={confirmPublish}
                isLoading={updaetePackageResult.isLoading}
                className="tw-bg-green-600 tw-text-white tw-px-4 tw-py-2 tw-text-sm tw-rounded-lg hover:tw-bg-green-700 tw-border-none"
                variant="none"
              >
                {t('PackageVersionList/confirm')}
              </PSButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WrappedErrorBoundary(PackageVersionList);
